import { getDB, PendingReview, VocabularyItem, SCHEMA_VERSION, getStoredSchemaVersion, setStoredSchemaVersion, clearUserData } from './db';
import { withReadDeadline } from '@/lib/readDeadline';
import { fetchAllVocabulary } from '@/lib/vocabularyReader';
import { supabase } from '@/integrations/supabase/client';

/**
 * Cache vocabulary items from Supabase to IndexedDB
 */
export async function cacheVocabulary(userId: string, items: VocabularyItem[]): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('vocabulary', 'readwrite');
  
  // Clear existing items for this user first
  const index = tx.store.index('by-user');
  let cursor = await index.openCursor(IDBKeyRange.only(userId));
  while (cursor) {
    await cursor.delete();
    cursor = await cursor.continue();
  }
  
  // Add new items
  for (const item of items) {
    await tx.store.put({ ...item, user_id: userId });
  }
  
  await tx.done;
  
  // Update last sync time
  await setLastSyncTime(userId, new Date());
}

/**
 * Get cached vocabulary from IndexedDB
 */
export async function getCachedVocabulary(userId: string): Promise<VocabularyItem[]> {
  const db = await getDB();
  const index = db.transaction('vocabulary', 'readonly').store.index('by-user');
  const items = await index.getAll(IDBKeyRange.only(userId));
  
  // Sort by created_at descending to match server order
  return items.sort((a, b) => 
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
}

/**
 * Check schema version and clear stale cache if needed
 * Called on app load to handle offline data schema migrations
 */
export async function checkAndMigrateSchemaVersion(userId: string): Promise<boolean> {
  const storedVersion = await getStoredSchemaVersion();
  
  if (storedVersion < SCHEMA_VERSION) {
    console.log(`[SyncManager] Schema version mismatch: stored=${storedVersion}, current=${SCHEMA_VERSION}`);
    console.log('[SyncManager] Clearing stale cache for schema migration...');
    
    // Clear outdated cache data
    await clearUserData(userId);
    
    // Update stored schema version
    await setStoredSchemaVersion(SCHEMA_VERSION);
    
    return true; // Indicates cache was cleared
  }
  
  return false;
}

/**
 * Sync vocabulary from server to local cache
 * Used for background sync and pre-caching on login
 */
export async function syncVocabularyFromServer(userId: string): Promise<boolean> {
  try {
    // Check and handle schema migration first
    const wasMigrated = await checkAndMigrateSchemaVersion(userId);
    if (wasMigrated) {
      console.log('[SyncManager] Schema was migrated, will re-sync from server');
    }
    
    // RFC 162: same complete, paginated, bounded reader as the foreground query —
    // a failed page throws, so a partial result never replaces the cache.
    const data = await withReadDeadline((signal) => fetchAllVocabulary(userId, signal));
    
    await cacheVocabulary(userId, data as Parameters<typeof cacheVocabulary>[1]);
    console.log(`[SyncManager] Vocabulary synced: ${data?.length || 0} items`);
    return true;
  } catch (error) {
    console.error('[SyncManager] Error syncing vocabulary from server:', error);
    return false;
  }
}

/**
 * Save a review to the pending sync queue
 */
export async function savePendingReview(review: Omit<PendingReview, 'id'>): Promise<void> {
  const db = await getDB();
  await db.add('pendingSync', review as PendingReview);
  
  // Register for Background Sync if supported
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await (registration as ServiceWorkerRegistration & { sync: { register: (tag: string) => Promise<void> } }).sync.register('sync-pending-reviews');
      console.log('[SyncManager] Background sync registered for pending reviews');
    } catch (e) {
      console.warn('[SyncManager] Background Sync registration failed:', e);
    }
  }
}

/**
 * Get count of pending reviews for a user
 */
export async function getPendingReviewCount(userId: string): Promise<number> {
  const db = await getDB();
  const index = db.transaction('pendingSync', 'readonly').store.index('by-user');
  return index.count(IDBKeyRange.only(userId));
}

/**
 * Get all pending reviews for a user
 */
export async function getPendingReviews(userId: string): Promise<PendingReview[]> {
  const db = await getDB();
  const index = db.transaction('pendingSync', 'readonly').store.index('by-user');
  return index.getAll(IDBKeyRange.only(userId));
}

/**
 * Sync pending reviews to Supabase
 * Returns the number of successfully synced reviews
 */
export async function syncPendingReviews(userId: string): Promise<number> {
  const pending = await getPendingReviews(userId);
  if (pending.length === 0) return 0;
  
  let synced = 0;
  const db = await getDB();
  
  for (const review of pending) {
    try {
      // Update the vocabulary item in Supabase
      const { error } = await supabase
        .from('vocabulary')
        .update({
          interval_days: review.updatedFields.interval_days,
          ease_factor: review.updatedFields.ease_factor,
          repetitions: review.updatedFields.repetitions,
          next_review_at: review.updatedFields.next_review_at,
          last_reviewed_at: review.updatedFields.last_reviewed_at,
          stability: review.updatedFields.stability ?? null,
          difficulty: review.updatedFields.difficulty ?? null,
          state: review.updatedFields.state ?? null,
          lapses: review.updatedFields.lapses ?? null,
          learning_steps: review.updatedFields.learning_steps ?? null,
        })
        .eq('id', review.cardId)
        .eq('user_id', userId);
      
      if (error) {
        console.error('Error syncing review:', error);
        continue;
      }
      
      // Track the review stat
      await supabase.rpc('increment_study_stat', {
        p_user_id: userId,
        p_stat_name: 'cards_reviewed',
        p_increment: 1
      });
      
      // Track the rating stat
      await supabase.rpc('increment_study_stat', {
        p_user_id: userId,
        p_stat_name: `rating_${review.rating}`,
        p_increment: 1
      });
      
      // Delete the synced review from pending queue
      if (review.id !== undefined) {
        await db.delete('pendingSync', review.id);
      }
      
      synced++;
    } catch (error) {
      console.error('Error syncing review:', error);
    }
  }
  
  return synced;
}

/**
 * Update a single vocabulary item in local cache
 */
export async function updateLocalVocabulary(
  cardId: string, 
  updates: Partial<VocabularyItem>
): Promise<void> {
  const db = await getDB();
  const existing = await db.get('vocabulary', cardId);
  
  if (existing) {
    await db.put('vocabulary', { ...existing, ...updates });
  }
}

/**
 * Delete a vocabulary item from local cache
 */
export async function deleteLocalVocabulary(cardId: string): Promise<void> {
  const db = await getDB();
  await db.delete('vocabulary', cardId);
}

/**
 * Get last sync timestamp for a user
 */
export async function getLastSyncTime(userId: string): Promise<Date | null> {
  const db = await getDB();
  const state = await db.get('appState', `lastSync-${userId}`);
  if (state?.value) {
    return new Date(state.value as string);
  }
  return null;
}

/**
 * Set last sync timestamp for a user
 */
export async function setLastSyncTime(userId: string, time: Date): Promise<void> {
  const db = await getDB();
  await db.put('appState', {
    key: `lastSync-${userId}`,
    value: time.toISOString(),
  });
}

/**
 * Get count of due cards from local IndexedDB cache
 * Used for offline mode badge count in BottomNav
 */
export async function getLocalDueCount(userId: string): Promise<number> {
  const db = await getDB();
  const index = db.transaction('vocabulary', 'readonly').store.index('by-user');
  const items = await index.getAll(IDBKeyRange.only(userId));
  
  const now = new Date();
  return items.filter(item => 
    !item.next_review_at || new Date(item.next_review_at) <= now
  ).length;
}
